/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import {
  ContactCustomerType,
  EmailCampaignRestService,
  SalesFolderStatus,
} from '@gh/builder-shared-data';
import {
  DictionaryService,
  getRouteParam,
  globalDictionaries,
  InitViewService,
  isRouteForCreationMode,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { map$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import {
  EmailCampaignAccountInfo,
  EmailCampaignDetails,
  emailCampaignDetailsFromDto,
  emailCampaignDetailsInit,
  emailCampaignDetailsToDto,
  emailCampaignDetailsToFilterDto,
  EmailCampaignRecipient,
} from './email-campaigns.model';

@Injectable()
export class EmailCampaignDetailsBusinessService implements InitViewService {
  constructor(private emailCampaignRest: EmailCampaignRestService,
              private dictionaries: DictionaryService) {
  }

  initView(route: ActivatedRouteSnapshot): any {
    return {
      salesFolderStatusDict: this.dictionaries.getByEnum(SalesFolderStatus),
      customerTypeDict: this.dictionaries.getByEnum(ContactCustomerType),
      salesFolderStageDict: this.dictionaries.getByEnumGlobal(globalDictionaries.stage),
      priorityDict: this.dictionaries.getByEnumGlobal(globalDictionaries.priority),
      pricingPlanType: this.getAccountInfo(),
      details: isRouteForCreationMode(route)
        ? this.init()
        : this.get(getRouteParam(route, 'campaignId')),
    };
  }

  init(): Observable<EmailCampaignDetails> {
    const details = emailCampaignDetailsInit();
    return this.findAllRecipients(details).pipe(map$((recipients) => ({
      ...details,
      recipients,
    })));
  }

  get(campaignId: ID): Observable<EmailCampaignDetails> {
    return this.emailCampaignRest.get(campaignId).pipe(map$(emailCampaignDetailsFromDto));
  }

  save(details: EmailCampaignDetails, pricingPlanType: EmailCampaignAccountInfo): Observable<EmailCampaignDetails> {
    return details.campaignId
      ? this.emailCampaignRest.update(
        emailCampaignDetailsToDto(details, pricingPlanType))
        .pipe(map$(emailCampaignDetailsFromDto))
      : this.emailCampaignRest.create(
        emailCampaignDetailsToDto(details, pricingPlanType))
        .pipe(map$(emailCampaignDetailsFromDto));
  }

  findAllRecipients(details: EmailCampaignDetails): Observable<EmailCampaignRecipient[]> {
    return this.emailCampaignRest.findAllRecipients(emailCampaignDetailsToFilterDto(details));
  }

  remove(details: EmailCampaignDetails, markAsDeleted: boolean = false): Observable<void> {
    return this.emailCampaignRest.remove(details.campaignId, details.version!, markAsDeleted);
  }

  getAccountInfo(): Observable<EmailCampaignAccountInfo> {
    return this.emailCampaignRest.getAccountInfo();
  }
}
